// ============================================
// Internationalization Types
// ============================================

/**
 * I18n configuration for content
 */
export interface I18nConfig {
    /** Default language code */
    defaultLanguage: string;
    /** Language strings by language code */
    languages: Record<string, I18nStrings>;
}

/**
 * Translatable strings for a language
 */
export type I18nStrings = Record<string, string>;

/**
 * Common i18n keys used across content types
 * Extends Record<string, string> to allow indexing by string
 */
export interface CommonI18nKeys extends Record<string, string> {
    submit: string;
    retry: string;
    next: string;
    previous: string;
    hint: string;
    correct: string;
    incorrect: string;
    partiallyCorrect: string;
    timeUp: string;
    selectAnswer: string;
    loading: string;
    error: string;
    dismiss: string;
    progress: string;
    scorePerfect: string;
    scoreGreat: string;
    scoreGood: string;
    scorePractice: string;
    scoreTryAgain: string;
    scoreResult: string;
    submitting: string;
    submitFailed: string;
    gradingUnavailable: string;
    save: string;
    validationErrors: string;
    preview: string;
    previewNotAvailable: string;
    contentTab: string;
    configurationTab: string;
    unknownContentType: string;
    missingComponent: string;
    noEditComponentForType: string;
    noPlayerComponentForType: string;
    untitledSection: string;
    sectionTitle: string;
    answerAtLeastOneQuestion: string;
    answerAllQuestions: string;
}

/**
 * Default English translations
 */
export const DEFAULT_EN_STRINGS: CommonI18nKeys = {
    submit: 'Submit',
    retry: 'Try Again',
    next: 'Next',
    previous: 'Previous',
    hint: 'Show Hint',
    correct: 'Correct!',
    incorrect: 'Incorrect',
    partiallyCorrect: 'Partially Correct',
    timeUp: 'Time is up!',
    selectAnswer: 'Please select an answer',
    loading: 'Loading...',
    error: 'An error occurred',
    dismiss: 'Dismiss',
    progress: 'Progress',
    scorePerfect: 'Perfect!',
    scoreGreat: 'Great job!',
    scoreGood: 'Good effort!',
    scorePractice: 'Keep practicing!',
    scoreTryAgain: 'Try again!',
    scoreResult: 'You got {correct} of {total} correct',
    submitting: 'Submitting...',
    submitFailed: 'Submit failed',
    gradingUnavailable: "We couldn't grade your answer yet. Please try again later.",
    save: 'Save',
    validationErrors: 'Validation Errors',
    preview: 'Preview',
    previewNotAvailable: 'Preview not available',
    contentTab: 'Content',
    configurationTab: 'Configuration',
    unknownContentType: 'Unknown content type',
    missingComponent: 'Missing component',
    noEditComponentForType: 'No edit component registered for "{type}"',
    noPlayerComponentForType: 'No player component registered for "{type}"',
    untitledSection: 'Untitled section',
    sectionTitle: 'Section title',
    answerAtLeastOneQuestion: 'Answer at least one question to submit',
    answerAllQuestions: 'Answer all questions to submit',
};

/**
 * Default Vietnamese translations
 */
export const DEFAULT_VI_STRINGS: CommonI18nKeys = {
    submit: 'Gửi',
    retry: 'Thử lại',
    next: 'Tiếp theo',
    previous: 'Trước',
    hint: 'Xem gợi ý',
    correct: 'Chính xác!',
    incorrect: 'Chưa đúng',
    partiallyCorrect: 'Đúng một phần',
    timeUp: 'Hết thời gian!',
    selectAnswer: 'Vui lòng chọn câu trả lời',
    loading: 'Đang tải...',
    error: 'Đã xảy ra lỗi',
    dismiss: 'Bỏ qua',
    progress: 'Tiến độ',
    scorePerfect: 'Hoàn hảo!',
    scoreGreat: 'Làm tốt lắm!',
    scoreGood: 'Cố gắng tốt!',
    scorePractice: 'Tiếp tục luyện tập!',
    scoreTryAgain: 'Thử lại!',
    scoreResult: 'Bạn trả lời đúng {correct}/{total}',
    submitting: 'Đang gửi...',
    submitFailed: 'Gửi thất bại',
    gradingUnavailable: 'Chưa chấm được bài của bạn, vui lòng thử lại sau.',
    save: 'Lưu',
    validationErrors: 'Lỗi xác thực',
    preview: 'Xem trước',
    previewNotAvailable: 'Không có bản xem trước',
    contentTab: 'Nội dung',
    configurationTab: 'Cấu hình',
    unknownContentType: 'Loại nội dung không xác định',
    missingComponent: 'Thiếu thành phần',
    noEditComponentForType: 'Không tìm thấy thành phần chỉnh sửa cho "{type}"',
    noPlayerComponentForType: 'Không tìm thấy thành phần hiển thị cho "{type}"',
    untitledSection: 'Mục chưa đặt tên',
    sectionTitle: 'Tiêu đề mục',
    answerAtLeastOneQuestion: 'Trả lời ít nhất một câu hỏi để gửi',
    answerAllQuestions: 'Trả lời tất cả câu hỏi để gửi',
};

// ============================================
// Supported locales (product scope: en + vi only)
// ============================================

export const SUPPORTED_CONTENT_LOCALES = ['en', 'vi'] as const;

export type SupportedContentLocale = (typeof SUPPORTED_CONTENT_LOCALES)[number];

/**
 * Coerce any stored locale code to a supported learner/author UI locale.
 */
export function normalizeContentLocale(code: string | undefined | null): SupportedContentLocale {
    if (code === 'vi') {
        return 'vi';
    }
    return 'en';
}

/**
 * Merge embed/parent UI language into content config (`i18n.defaultLanguage`).
 */
export function applyEmbedLanguageToConfig(
    config: import('./config').ContentConfig | undefined,
    language: string | undefined | null,
): import('./config').ContentConfig | undefined {
    if (!language?.trim()) {
        return config;
    }
    const defaultLanguage = normalizeContentLocale(language);
    if (!config) {
        return { i18n: { defaultLanguage, languages: {} } };
    }
    return {
        ...config,
        i18n: {
            defaultLanguage,
            languages: config.i18n?.languages ?? {},
        },
    };
}

/**
 * Per-element string maps (en + vi) registered by content-elements (or apps).
 * Merged into {@link getDefaultI18nConfig} / {@link getDefaultI18n} after core defaults.
 */
export interface ElementI18nRegistrationBundle {
    en: I18nStrings;
    vi: I18nStrings;
}

const elementI18nRegistrations: ElementI18nRegistrationBundle[] = [];

let elementI18nMergeCache: ElementI18nRegistrationBundle | null = null;
let elementI18nMergeCacheLen = -1;

/**
 * Register element/block i18n so core default configs and `useLocale` include these keys.
 * Call from each element's `schema.ts` (or barrel) at module load. Safe to call multiple times.
 */
export function registerElementI18nBundle(bundle: ElementI18nRegistrationBundle): void {
    elementI18nRegistrations.push(bundle);
    elementI18nMergeCache = null;
    elementI18nMergeCacheLen = -1;
}

export function getRegisteredElementI18nBundleCount(): number {
    return elementI18nRegistrations.length;
}

/** Merge all registered element bundles (later registrations override keys). */
export function mergeRegisteredElementI18nBundles(): ElementI18nRegistrationBundle {
    if (elementI18nMergeCache && elementI18nMergeCacheLen === elementI18nRegistrations.length) {
        return elementI18nMergeCache;
    }
    const en: I18nStrings = {};
    const vi: I18nStrings = {};
    for (const b of elementI18nRegistrations) {
        Object.assign(en, b.en);
        Object.assign(vi, b.vi);
    }
    elementI18nMergeCache = { en, vi };
    elementI18nMergeCacheLen = elementI18nRegistrations.length;
    return elementI18nMergeCache;
}

/**
 * Per-element / shared editor + player strings (English).
 * Keys are merged into DEFAULT_I18N language maps and overridden by content config.
 */
export interface ElementEditorPlayerI18nKeys extends Record<string, string> {
    // Shared editor
    add: string;
    remove: string;
    upload: string;
    optionalSectionTitle: string;
    addSection: string;
    addChoice: string;
    choices: string;
    question: string;
    explanation: string;
    correctAnswer: string;
    enterText: string;
    enterContent: string;
    altText: string;
    uploadImage: string;
    uploadAudio: string;
    uploadVideo: string;
    uploadMedia: string;
    image: string;
    play: string;
    pause: string;
    record: string;
    stop: string;
    enterQuestionText: string;
    enterChoiceText: string;
    choiceFeedbackPlaceholder: string;
    explainCorrectAnswer: string;
    enterItem: string;
    enterOption: string;
    addPair: string;
    correctAnswersSemicolonHint: string;
    blankModeDropdown: string;
    blankModeDrag: string;
    blankModeType: string;
    choosePlaceholder: string;
    fitbDragInstructions: string;
    fitbRemoveAnswerAria: string;
    enterQuestionInstruction: string;
    addItem: string;
    removeItem: string;
    dragToReorder: string;
    addZone: string;
    selectColor: string;
    dropHere: string;
    addHotspot: string;
    clickHotspot: string;
    dragToMoveAnswer: string;
    removeAnswer: string;
    enterDescription: string;
    configuration: string;
    lesson: string;
    content: string;
    lessonContent: string;
    questions: string;
    answerChoices: string;
    choiceLabel: string;
    multipleCorrectAnswers: string;
    shuffleChoicesEditor: string;
    mcqSelectCorrectAnswer: string;

    /** Match editor */
    itemsToMatch: string;
    matchingOptionsHeading: string;
    addQuestionItem: string;
    addMatchingOption: string;

    /** Ordering editor */
    moveUp: string;
    moveDown: string;
    explanationAfterSubmit: string;

    /** FITB editor */
    fillInTheBlanks: string;
    answerMode: string;
    shuffleOptionsFITB: string;
    shuffleFITBDesc: string;
    caseSensitiveFITB: string;
    caseSensitiveDesc: string;
    passageLabelFITB: string;
    passageFITBHint: string;
    blanksHeading: string;
    blanksEmptyFITB: string;
    blankNth: string;
    acceptedAnswers: string;
    wrongOptionsFITB: string;
    wrongOptionsSemicolonHint: string;

    /** Image / audio / video editors */
    describeImage: string;
    caption: string;
    imageCaptionPlaceholder: string;
    size: string;
    alignment: string;
    alignLeft: string;
    alignCenter: string;
    alignRight: string;
    sizeSmall: string;
    sizeMedium: string;
    sizeLarge: string;
    sizeFull: string;
    audioSource: string;
    videoSource: string;
    audioTitlePlaceholder: string;
    videoTitlePlaceholder: string;
    posterImage: string;
    posterImageHint: string;
    videoPosterMediaTitle: string;
    aspectRatio: string;
    aspect169: string;
    aspect43: string;
    aspect11: string;
    aspect916: string;
    playbackOptions: string;
    autoplay: string;
    loop: string;
    muted: string;
    showControls: string;

    /** Record audio editor */
    recordAudioHeading: string;
    recordAudioRecordingTime: string;
    recordAudioSecondsHint: string;
    recordAudioAutoGrading: string;
    recordAudioQuestionPlaceholder: string;

    /** Record audio player */
    recordAudioTapToStart: string;
    recordAudioStartRecording: string;
    recordAudioStopRecording: string;
    recordAudioRerecord: string;
    recordAudioMicDenied: string;
    recordAudioMicUnavailable: string;
    recordAudioNoData: string;
    recordAudioNoRecordingAvailable: string;

    /** MCQ player */
    mcqPlayerNoData: string;
    mcqPlayerNoChoices: string;
    mcqPlayerCorrectIndicator: string;

    /** Match player */
    matchPlayerNoData: string;
    matchPlayerInstruction: string;

    /** Ordering player */
    orderingPlayerNoData: string;

    /** Page editor */
    pageDescriptionOptional: string;
    pageTitlePlaceholder: string;
    pageDescriptionPlaceholder: string;
    pageSections: string;
    pageNoSectionsYet: string;

    /** Composite question registry / sidebar */
    questionTypeMatch: string;
    questionTypeOrdering: string;
    questionTypeRecordAudio: string;
    removeSection: string;
    removeQuestion: string;

    /** Media editor (upload widget) */
    fromLink: string;
    removeMedia: string;
    replaceMedia: string;
    maxSizeLabel: string;
    dropZoneImage: string;
    dropZoneAudio: string;
    dropZoneVideo: string;
    enterImageUrl: string;
    enterAudioUrl: string;
    enterVideoUrl: string;
    fileSizeExceedsMB: string;
    uploadFailedShort: string;
    invalidFileTypeFor: string;

    ok: string;
}

export const DEFAULT_ELEMENT_EN_STRINGS: ElementEditorPlayerI18nKeys = {
    add: 'Add',
    remove: 'Remove',
    upload: 'Upload',
    optionalSectionTitle: 'Optional section title',
    addSection: 'Add section',
    choices: 'Choices',
    question: 'Question',
    explanation: 'Explanation',
    correctAnswer: 'Correct answer',
    enterText: 'Enter your text here...',
    enterContent: 'Enter content...',
    altText: 'Alt text',
    uploadImage: 'Upload image',
    uploadAudio: 'Upload audio',
    uploadVideo: 'Upload video',
    uploadMedia: 'Upload media',
    image: 'Image',
    play: 'Play',
    pause: 'Pause',
    record: 'Record',
    stop: 'Stop',
    enterQuestionText: 'Enter question text',
    enterChoiceText: 'Enter choice text',
    choiceFeedbackPlaceholder: 'Feedback for this choice...',
    explainCorrectAnswer: 'Explain the correct answer...',
    enterItem: 'Enter item...',
    enterOption: 'Enter option...',
    addPair: 'Add pair',
    correctAnswersSemicolonHint: 'Correct answer(s); separate with semicolons',
    blankModeDropdown: 'Dropdown',
    blankModeDrag: 'Drag & drop',
    blankModeType: 'Type (free text)',
    choosePlaceholder: 'Choose…',
    fitbDragInstructions: 'Fill in the blanks with the correct answers',
    fitbRemoveAnswerAria: 'Remove answer for blank {n}',
    enterQuestionInstruction: 'Enter question instruction...',
    addItem: 'Add item',
    removeItem: 'Remove item',
    dragToReorder: 'Drag to reorder',
    addZone: 'Add zone',
    selectColor: 'Select color',
    dropHere: 'Drop here',
    addHotspot: 'Add hotspot',
    clickHotspot: 'Click hotspot',
    dragToMoveAnswer: 'Drag to move answer',
    removeAnswer: 'Remove answer',
    enterDescription: 'Enter description...',
    configuration: 'Configuration',
    lesson: 'Lesson',
    content: 'Content',
    lessonContent: 'Lesson Content',
    // EAA-1772: composite editor tab/field labels (lesson-mcq, rich-matching)
    // must resolve from the shared default bundle so they never fall back to the
    // raw key when the editor's config.i18n omits the content type's own bundle.
    lessonTab: 'Lesson',
    descriptionOptional: 'Description (Optional)',
    enterTitlePlaceholder: 'Enter title',
    // EAA-2084: inline "title required" under an editor's title box.
    titleRequired: 'Please enter a title',
    enterDescriptionPlaceholder: 'Enter description or instructions for learners',
    questions: 'Questions',
    answerChoices: 'Answer Choices',
    choiceLabel: 'Choice',
    addChoice: 'Add Choice',
    multipleCorrectAnswers: 'Multiple Correct Answers',
    shuffleChoicesEditor: 'Shuffle Choices',
    mcqSelectCorrectAnswer: 'Select at least one correct answer.',
    itemsToMatch: 'Items to Match',
    matchingOptionsHeading: 'Options',
    addQuestionItem: 'Add Item',
    addMatchingOption: 'Add Option',
    moveUp: 'Move up',
    moveDown: 'Move down',
    explanationAfterSubmit: 'Explanation (after submit)',
    fillInTheBlanks: 'Fill in the blanks',
    answerMode: 'Answer mode',
    shuffleOptionsFITB: 'Shuffle options',
    shuffleFITBDesc: 'Randomize chips and dropdown order',
    caseSensitiveFITB: 'Case sensitive',
    caseSensitiveDesc: 'Typed answers must match capitalization',
    passageLabelFITB: 'Passage',
    passageFITBHint:
        'Use three or more underscores (___) for each blank. Tables and formatting are supported.',
    blanksHeading: 'Blanks',
    blanksEmptyFITB: 'Add ___ markers in the passage to create blanks.',
    blankNth: 'Blank',
    acceptedAnswers: 'Accepted answers',
    wrongOptionsFITB: 'Wrong options',
    wrongOptionsSemicolonHint: 'Wrong options; separate with semicolons',
    describeImage: 'Describe the image...',
    caption: 'Caption',
    imageCaptionPlaceholder: 'Image caption...',
    size: 'Size',
    alignment: 'Alignment',
    alignLeft: 'Left',
    alignCenter: 'Center',
    alignRight: 'Right',
    sizeSmall: 'Small (25%)',
    sizeMedium: 'Medium (50%)',
    sizeLarge: 'Large (75%)',
    sizeFull: 'Full Width (100%)',
    audioSource: 'Audio source',
    videoSource: 'Video source',
    audioTitlePlaceholder: 'Audio title...',
    videoTitlePlaceholder: 'Video title...',
    posterImage: 'Poster image',
    posterImageHint: 'Thumbnail shown before video plays',
    videoPosterMediaTitle: 'Video poster',
    aspectRatio: 'Aspect ratio',
    aspect169: '16:9 (Widescreen)',
    aspect43: '4:3 (Standard)',
    aspect11: '1:1 (Square)',
    aspect916: '9:16 (Portrait)',
    playbackOptions: 'Playback options',
    autoplay: 'Autoplay',
    loop: 'Loop',
    muted: 'Muted',
    showControls: 'Controls',
    recordAudioHeading: 'Audio recording',
    recordAudioRecordingTime: 'Recording time (seconds)',
    recordAudioSecondsHint: 'seconds (10-300s)',
    recordAudioAutoGrading: 'Auto-grading',
    recordAudioQuestionPlaceholder: 'Describe the recording task for learners…',
    recordAudioTapToStart: 'Tap to start recording',
    recordAudioStartRecording: 'Start recording',
    recordAudioStopRecording: 'Stop recording',
    recordAudioRerecord: 'Re-record',
    recordAudioMicDenied:
        'Microphone access was denied. Please allow microphone permission.',
    recordAudioMicUnavailable:
        'Could not access microphone. Please check your device settings.',
    recordAudioNoData: 'No recording data',
    recordAudioNoRecordingAvailable: 'No recording available',
    mcqPlayerNoData: 'No question data',
    mcqPlayerNoChoices: 'No choices available',
    mcqPlayerCorrectIndicator: '← Correct',
    matchPlayerNoData: 'No matching data',
    matchPlayerInstruction: 'Match each item with an option',
    orderingPlayerNoData: 'No ordering data',
    pageDescriptionOptional: 'Description (optional)',
    pageTitlePlaceholder: 'Page title',
    pageDescriptionPlaceholder: 'Page description',
    pageSections: 'Content sections',
    pageNoSectionsYet: 'No content sections yet. Add the first section to get started.',
    questionTypeMatch: 'Matching',
    questionTypeOrdering: 'Ordering',
    questionTypeRecordAudio: 'Record audio',
    removeSection: 'Remove section',
    removeQuestion: 'Remove question',
    fromLink: 'Link',
    removeMedia: 'Remove',
    replaceMedia: 'Replace',
    maxSizeLabel: 'Max size:',
    dropZoneImage: 'Drop image here or click to upload',
    dropZoneAudio: 'Drop audio here or click to upload',
    dropZoneVideo: 'Drop video here or click to upload',
    enterImageUrl: 'Enter image URL...',
    enterAudioUrl: 'Enter audio URL...',
    enterVideoUrl: 'Enter video URL...',
    fileSizeExceedsMB: 'File size exceeds maximum ({mb}MB)',
    uploadFailedShort: 'Upload failed',
    invalidFileTypeFor: 'Invalid file type. Please select a {type} file.',
    ok: 'OK',
};

export const DEFAULT_ELEMENT_VI_STRINGS: ElementEditorPlayerI18nKeys = {
    add: 'Thêm',
    remove: 'Xóa',
    upload: 'Tải lên',
    optionalSectionTitle: 'Tiêu đề mục (tuỳ chọn)',
    addSection: 'Thêm mục',
    addChoice: 'Thêm lựa chọn',
    choices: 'Các lựa chọn',
    question: 'Câu hỏi',
    explanation: 'Giải thích',
    correctAnswer: 'Đáp án đúng',
    enterText: 'Nhập văn bản của bạn...',
    enterContent: 'Nhập nội dung...',
    altText: 'Văn bản thay thế',
    uploadImage: 'Tải ảnh lên',
    uploadAudio: 'Tải âm thanh lên',
    uploadVideo: 'Tải video lên',
    uploadMedia: 'Tải tệp media lên',
    image: 'Hình ảnh',
    play: 'Phát',
    pause: 'Tạm dừng',
    record: 'Ghi âm',
    stop: 'Dừng',
    enterQuestionText: 'Nhập nội dung câu hỏi',
    enterChoiceText: 'Nhập nội dung lựa chọn',
    choiceFeedbackPlaceholder: 'Phản hồi cho lựa chọn này...',
    explainCorrectAnswer: 'Giải thích đáp án đúng...',
    enterItem: 'Nhập mục...',
    enterOption: 'Nhập đáp án...',
    addPair: 'Thêm cặp',
    correctAnswersSemicolonHint: 'Đáp án đúng; phân tách bằng dấu chấm phẩy',
    blankModeDropdown: 'Chọn danh sách',
    blankModeDrag: 'Kéo & thả',
    blankModeType: 'Nhập (văn bản tự do)',
    choosePlaceholder: 'Chọn…',
    fitbDragInstructions: 'Điền vào chỗ trống bằng các đáp án đúng',
    fitbRemoveAnswerAria: 'Xóa đáp án cho chỗ trống {n}',
    enterQuestionInstruction: 'Nhập hướng dẫn câu hỏi...',
    addItem: 'Thêm mục',
    removeItem: 'Xóa mục',
    dragToReorder: 'Kéo để sắp xếp',
    addZone: 'Thêm vùng',
    selectColor: 'Chọn màu',
    dropHere: 'Thả vào đây',
    addHotspot: 'Thêm điểm chạm',
    clickHotspot: 'Chọn điểm chạm',
    dragToMoveAnswer: 'Kéo để di chuyển đáp án',
    removeAnswer: 'Xóa đáp án',
    enterDescription: 'Nhập mô tả...',
    configuration: 'Cấu hình',
    lesson: 'Bài học',
    content: 'Nội dung',
    lessonContent: 'Nội dung bài học',
    // EAA-1772: xem chú thích ở bản en — nhãn tab/trường của trình soạn thảo tổ hợp.
    lessonTab: 'Bài học',
    descriptionOptional: 'Mô tả (tuỳ chọn)',
    enterTitlePlaceholder: 'Nhập tiêu đề',
    // EAA-2084: xem chú thích ở bản en.
    titleRequired: 'Vui lòng nhập tiêu đề',
    enterDescriptionPlaceholder: 'Nhập mô tả hoặc hướng dẫn cho học viên',
    questions: 'Câu hỏi',
    answerChoices: 'Các lựa chọn trả lời',
    choiceLabel: 'Lựa chọn',
    multipleCorrectAnswers: 'Nhiều đáp án đúng',
    shuffleChoicesEditor: 'Xáo trộn các lựa chọn',
    mcqSelectCorrectAnswer: 'Phải tích chọn ít nhất một đáp án đúng.',
    itemsToMatch: 'Mục cần ghép',
    matchingOptionsHeading: 'Tùy chọn',
    addQuestionItem: 'Thêm mục',
    addMatchingOption: 'Thêm tùy chọn',
    moveUp: 'Di chuyển lên',
    moveDown: 'Di chuyển xuống',
    explanationAfterSubmit: 'Giải thích (sau khi nộp)',
    fillInTheBlanks: 'Điền vào chỗ trống',
    answerMode: 'Chế độ trả lời',
    shuffleOptionsFITB: 'Xáo trộn tùy chọn',
    shuffleFITBDesc: 'Xáo trộn ngẫu nhiên thứ tự chip và dropdown',
    caseSensitiveFITB: 'Phân biệt chữ hoa chữ thường',
    caseSensitiveDesc: 'Câu trả lời phải đúng chữ hoa/thường',
    passageLabelFITB: 'Đoạn văn',
    passageFITBHint:
        'Dùng ba hoặc nhiều gạch dưới (___) cho mỗi chỗ trống. Hỗ trợ bảng và định dạng.',
    blanksHeading: 'Chỗ trống',
    blanksEmptyFITB: 'Thêm dấu ___ vào đoạn văn để tạo chỗ trống.',
    blankNth: 'Chỗ trống',
    acceptedAnswers: 'Đáp án chấp nhận',
    wrongOptionsFITB: 'Tùy chọn sai',
    wrongOptionsSemicolonHint: 'Tùy chọn sai; phân cách bằng dấu chấm phẩy',
    describeImage: 'Mô tả hình ảnh...',
    caption: 'Chú thích',
    imageCaptionPlaceholder: 'Chú thích ảnh...',
    size: 'Kích thước',
    alignment: 'Căn chỉnh',
    alignLeft: 'Trái',
    alignCenter: 'Giữa',
    alignRight: 'Phải',
    sizeSmall: 'Nhỏ (25%)',
    sizeMedium: 'Vừa (50%)',
    sizeLarge: 'Lớn (75%)',
    sizeFull: 'Toàn chiều rộng (100%)',
    audioSource: 'Nguồn âm thanh',
    videoSource: 'Nguồn video',
    audioTitlePlaceholder: 'Tiêu đề âm thanh...',
    videoTitlePlaceholder: 'Tiêu đề video...',
    posterImage: 'Ảnh poster',
    posterImageHint: 'Hình thu nhỏ hiển thị trước khi phát video',
    videoPosterMediaTitle: 'Poster video',
    aspectRatio: 'Tỷ lệ khung hình',
    aspect169: '16:9 (Màn rộng)',
    aspect43: '4:3 (Tiêu chuẩn)',
    aspect11: '1:1 (Vuông)',
    aspect916: '9:16 (Dọc)',
    playbackOptions: 'Tùy chọn phát',
    autoplay: 'Tự động phát',
    loop: 'Lặp lại',
    muted: 'Tắt tiếng',
    showControls: 'Điều khiển',
    recordAudioHeading: 'Ghi âm',
    recordAudioRecordingTime: 'Thời gian ghi (giây)',
    recordAudioSecondsHint: 'giây (10-300s)',
    recordAudioAutoGrading: 'Tự động chấm điểm',
    recordAudioQuestionPlaceholder: 'Mô tả nhiệm vụ ghi âm cho học viên…',
    recordAudioTapToStart: 'Chạm để bắt đầu ghi',
    recordAudioStartRecording: 'Bắt đầu ghi',
    recordAudioStopRecording: 'Dừng ghi',
    recordAudioRerecord: 'Ghi lại',
    recordAudioMicDenied: 'Quyền mic bị từ chối. Vui lòng cho phép truy cập mic.',
    recordAudioMicUnavailable:
        'Không truy cập được mic. Vui lòng kiểm tra thiết bị.',
    recordAudioNoData: 'Không có dữ liệu ghi âm',
    recordAudioNoRecordingAvailable: 'Chưa có bản ghi',
    mcqPlayerNoData: 'Không có dữ liệu câu hỏi',
    mcqPlayerNoChoices: 'Không có lựa chọn nào',
    mcqPlayerCorrectIndicator: '← Đúng',
    matchPlayerNoData: 'Không có dữ liệu ghép cặp',
    matchPlayerInstruction: 'Ghép mỗi mục với một đáp án',
    orderingPlayerNoData: 'Không có dữ liệu sắp xếp',
    pageDescriptionOptional: 'Mô tả (tuỳ chọn)',
    pageTitlePlaceholder: 'Tiêu đề trang',
    pageDescriptionPlaceholder: 'Mô tả trang',
    // EAA-2031: "Các mục (0)" beside "Câu hỏi (5)" read as a second question count;
    // these are content sections, so the label says so.
    pageSections: 'Phần nội dung',
    pageNoSectionsYet: 'Chưa có phần nội dung nào. Thêm phần đầu tiên để bắt đầu.',
    questionTypeMatch: 'Ghép cặp',
    questionTypeOrdering: 'Sắp thứ tự',
    questionTypeRecordAudio: 'Ghi âm',
    removeSection: 'Xóa mục',
    removeQuestion: 'Xóa câu hỏi',
    fromLink: 'Liên kết',
    removeMedia: 'Xóa',
    replaceMedia: 'Thay thế',
    maxSizeLabel: 'Dung lượng tối đa:',
    dropZoneImage: 'Thả ảnh vào đây hoặc bấm để tải lên',
    dropZoneAudio: 'Thả âm thanh vào đây hoặc bấm để tải lên',
    dropZoneVideo: 'Thả video vào đây hoặc bấm để tải lên',
    enterImageUrl: 'Nhập URL ảnh...',
    enterAudioUrl: 'Nhập URL âm thanh...',
    enterVideoUrl: 'Nhập URL video...',
    fileSizeExceedsMB: 'Dung lượng vượt quá tối đa ({mb}MB)',
    uploadFailedShort: 'Tải lên thất bại',
    invalidFileTypeFor:
        'Định dạng tệp không hợp lệ. Vui lòng chọn tệp {type}.',
    ok: 'OK',
};

/**
 * Alias: element-level i18n uses the same shape as content {@link I18nConfig}.
 */
export type ElementI18nConfig = I18nConfig;
